"use client";

import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, RefreshCw, Home, WifiOff } from "lucide-react";
import { ErrorScreen } from "@/components/virtualspace/error-screen";
import { StaleDeploymentErrorScreen } from "@/components/virtualspace/stale-deployment-error-screen";
import { isStaleDeploymentError } from "@/lib/virtualspace/stale-deployment";
import { captureAnalyticsException } from "@/lib/analytics/posthog-client";
import { isTransientNetworkError } from "@/lib/virtualspace/transient-network-error";
import { useConnectionRetry } from "@/lib/virtualspace/use-connection-retry";

/**
 * Root-level error boundary. Catches errors thrown anywhere in
 * the app tree that weren't caught by a more-specific segment
 * boundary (e.g. `/dashboard/[accountSlug]/error.tsx`). Without
 * this, Next falls back to a generic white "An error occurred"
 * screen — which we never want a user to see.
 *
 * Note: `global-error.tsx` (sibling file) covers errors in the
 * root layout itself. This file covers everything below the
 * root layout.
 */
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const transient = isTransientNetworkError(error);
  const retry = useConnectionRetry(reset);

  useEffect(() => {
    console.error("[root error boundary]", error);
    captureAnalyticsException(
      error,
      transient ? { transient: true } : undefined,
    );
  }, [error, transient]);

  if (transient) {
    return (
      <ErrorScreen
        variant="offline"
        icon={WifiOff}
        immersive
        eyebrow="Connection lost"
        title="Connection lost."
        description="We couldn't reach the server just now. Check your connection, then retry."
        primaryAction={
          <button
            onClick={retry}
            className="vs-btn-solid inline-flex items-center gap-2 px-5 py-2.5 text-sm"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        }
      />
    );
  }

  if (isStaleDeploymentError(error)) {
    return <StaleDeploymentErrorScreen immersive />;
  }

  return (
    <ErrorScreen
      variant="error"
      icon={AlertTriangle}
      immersive
      eyebrow="Signal interrupted"
      title="We drifted off course."
      description="The app lost its bearings for a moment. Try recalibrating; if it keeps happening, support can help you get back on course."
      diagnostic={error.digest ? `Support code · ${error.digest}` : undefined}
      primaryAction={
        <button
          onClick={reset}
          className="vs-btn-solid inline-flex items-center gap-2 px-5 py-2.5 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          Recalibrate
        </button>
      }
      secondaryAction={
        <Link
          href="/"
          className="vs-btn-outline inline-flex items-center gap-2 px-5 py-2.5 text-sm"
        >
          <Home className="h-4 w-4" />
          Home base
        </Link>
      }
    />
  );
}
